import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getChatHistory, sendChatMessage } from '../../api/project/chat.js';
import { getTimeAgo } from '../../library/utilities/intl.js';
import styles from './passive-chat.module.scss';

export const OPEN_PASSIVE_CHAT_EVENT = 'passive-chat:open';

const FAB_SIZE = 56
const FAB_MARGIN = 24
const DRAG_THRESHOLD = 5

const clampPosition = ({ right, bottom }) => ({
    right: Math.min(Math.max(right, FAB_MARGIN), window.innerWidth - FAB_SIZE - FAB_MARGIN),
    bottom: Math.min(Math.max(bottom, FAB_MARGIN), window.innerHeight - FAB_SIZE - FAB_MARGIN),
})

const PassiveChat = ({ token, items = [], isApproved = false, hidden = false }) => {
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const [messages, setMessages] = useState(null)
    const [inputValue, setInputValue] = useState('')
    const [selectedItemId, setSelectedItemId] = useState(items.length === 1 ? items[0].id : null)
    const [loading, setLoading] = useState(false)
    const [sending, setSending] = useState(false)
    const [error, setError] = useState(null)
    const [justSent, setJustSent] = useState(false)
    const [position, setPosition] = useState({ right: FAB_MARGIN, bottom: FAB_MARGIN })
    const bodyRef = useRef(null)
    const dragState = useRef(null)
    const loadedItemIdRef = useRef(null)
    const checkedChatContextRef = useRef(null)

    const needsItemPicker = isApproved && items.length > 1
    const canSend = !needsItemPicker || Boolean(selectedItemId)
    const chatItemIdsKey = JSON.stringify(items.map(({ id }) => id))

    const loadHistory = useCallback(async (itemId, { silent = false } = {}) => {
        if (!silent) {
            setLoading(true)
        }
        setError(null)
        try {
            const data = await getChatHistory(token, itemId)
            setMessages(data)
            loadedItemIdRef.current = itemId
            return data
        } catch {
            setError(t('Could not load your messages. Please try again.'))
        } finally {
            if (!silent) {
                setLoading(false)
            }
        }
    }, [token, t])

    useEffect(() => {
        if (items.length === 1 && selectedItemId == null) {
            setSelectedItemId(items[0].id)
        }
    }, [items, selectedItemId])

    useEffect(() => {
        if (hidden || !token) {
            return undefined
        }
        const searchParams = new URLSearchParams(window.location.search)
        const shouldOpen = searchParams.get('open_chat') === 'true' || searchParams.get('open_chat') === '1' || searchParams.get('chat') === 'open'
        const urlItemId = searchParams.get('item_id')

        if (urlItemId && items.some((item) => String(item.id) === String(urlItemId))) {
            const numericItemId = Number(urlItemId)
            setSelectedItemId(numericItemId)
            loadHistory(numericItemId)
        }

        if (shouldOpen) {
            setIsOpen(true)
        }
    }, [hidden, token, items, loadHistory])

    useEffect(() => {
        if (isOpen && !needsItemPicker && loadedItemIdRef.current !== selectedItemId) {
            loadHistory(selectedItemId)
        }
    }, [isOpen, needsItemPicker, selectedItemId, loadHistory])

    useEffect(() => {
        if (hidden || !token) {
            return undefined
        }
        // `items` may be recreated by a parent render. Only perform this
        // automatic check once for each token/item combination; otherwise the
        // state update from a response can cause a request loop.
        const chatContextKey = `${token}:${needsItemPicker}:${chatItemIdsKey}`
        if (checkedChatContextRef.current === chatContextKey) {
            return undefined
        }
        checkedChatContextRef.current = chatContextKey

        let cancelled = false
        const chatItemIds = JSON.parse(chatItemIdsKey)
        const checkForMessages = async () => {
            try {
                if (needsItemPicker) {
                    const results = await Promise.all(
                        chatItemIds.map((itemId) => getChatHistory(token, itemId).catch(() => []))
                    )
                    if (!cancelled && results.some((m) => (m?.length ?? 0) > 0)) {
                        setIsOpen(true)
                    }
                } else {
                    const data = await loadHistory(selectedItemId)
                    if (!cancelled && (data?.length ?? 0) > 0) {
                        setIsOpen(true)
                    }
                }
            } catch {
                // ignore
            }
        }
        checkForMessages()
        return () => { cancelled = true }
    }, [hidden, token, needsItemPicker, chatItemIdsKey, selectedItemId, loadHistory])

    useEffect(() => {
        if (bodyRef.current) {
            bodyRef.current.scrollTop = bodyRef.current.scrollHeight
        }
    }, [messages, isOpen])

    useEffect(() => {
        if (hidden) {
            return undefined
        }
        const handler = () => setIsOpen(true)
        window.addEventListener(OPEN_PASSIVE_CHAT_EVENT, handler)
        return () => window.removeEventListener(OPEN_PASSIVE_CHAT_EVENT, handler)
    }, [hidden])

    useEffect(() => {
        const handleResize = () => setPosition((current) => clampPosition(current))
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    if (hidden) {
        return null
    }

    const openHandler = () => setIsOpen(true)
    const closeHandler = () => setIsOpen(false)

    const dragMoveHandler = (e) => {
        if (!dragState.current) {
            return
        }
        const point = e.touches ? e.touches[0] : e
        const deltaX = point.clientX - dragState.current.startX
        const deltaY = point.clientY - dragState.current.startY
        if (!dragState.current.dragging && Math.hypot(deltaX, deltaY) > DRAG_THRESHOLD) {
            dragState.current.dragging = true
        }
        if (dragState.current.dragging) {
            e.preventDefault()
            setPosition(clampPosition({
                right: dragState.current.startRight - deltaX,
                bottom: dragState.current.startBottom - deltaY,
            }))
        }
    }

    const dragEndHandler = () => {
        if (dragState.current?.dragging) {
            dragState.current.suppressClick = true
        }
        window.removeEventListener('mousemove', dragMoveHandler)
        window.removeEventListener('mouseup', dragEndHandler)
        window.removeEventListener('touchmove', dragMoveHandler)
        window.removeEventListener('touchend', dragEndHandler)
    }

    const dragStartHandler = (e) => {
        const point = e.touches ? e.touches[0] : e
        dragState.current = {
            startX: point.clientX,
            startY: point.clientY,
            startRight: position.right,
            startBottom: position.bottom,
            dragging: false,
            suppressClick: false,
        }
        window.addEventListener('mousemove', dragMoveHandler)
        window.addEventListener('mouseup', dragEndHandler)
        window.addEventListener('touchmove', dragMoveHandler, { passive: false })
        window.addEventListener('touchend', dragEndHandler)
    }

    const fabClickHandler = () => {
        if (dragState.current?.suppressClick) {
            dragState.current.suppressClick = false
            return
        }
        openHandler()
    }

    const PANEL_HEIGHT_ESTIMATE = 460
    const spaceAbove = window.innerHeight - position.bottom - FAB_SIZE
    const panelBottom = spaceAbove >= PANEL_HEIGHT_ESTIMATE
        ? position.bottom + FAB_SIZE + 8
        : Math.max(position.bottom - PANEL_HEIGHT_ESTIMATE - 8, FAB_MARGIN)

    const selectItem = (itemId) => {
        setSelectedItemId(itemId)
        loadHistory(itemId)
    }

    const submitHandler = async (e) => {
        e.preventDefault()
        const message = inputValue.trim()
        if (!message || !canSend || sending) {
            return
        }
        setSending(true)
        setError(null)
        try {
            await sendChatMessage(token, message, selectedItemId)
            setInputValue('')
            setJustSent(true)
            await loadHistory(selectedItemId, { silent: true })
        } catch {
            setError(t('Could not send your message. Please try again.'))
        } finally {
            setSending(false)
        }
    }

    return (
        <div className={styles.passiveChat}>
            <button
                className={styles.fab}
                style={{ right: position.right, bottom: position.bottom }}
                onMouseDown={dragStartHandler}
                onTouchStart={dragStartHandler}
                onClick={fabClickHandler}
                aria-label={t('Do you have a question?')}
            >
                <img src="/icons/chat.svg" alt="" />
            </button>
            {isOpen && (
                <div className={styles.panel} style={{ right: position.right, bottom: panelBottom }}>
                    <div className={styles.panelHeader}>
                        <span>{t('Do you have a question?')}</span>
                        <button className={styles.closeButton} onClick={closeHandler} aria-label={t('Close')}>&times;</button>
                    </div>
                    {needsItemPicker && !selectedItemId ? (
                        <div className={styles.itemPicker}>
                            <p>{t('Which item is your message about?')}</p>
                            <ul>
                                {items.map((item) => (
                                    <li key={item.id}>
                                        <button className={styles.itemPickerButton} onClick={() => selectItem(item.id)}>
                                            {item.name || `#${item.id}`}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : (
                        <>
                            <div className={styles.panelBody} ref={bodyRef}>
                                {loading && messages == null && <p className={styles.info}>{t('Loading...')}</p>}
                                {!loading && messages?.length === 0 && (
                                 <>
                                     <p className={styles.info}>{t('Feel free to send us a message. We usually respond within 8 hours on working days.')}</p>
                                    <p className={styles.info}>{t('Do you have an urgent question or need a quick response? Please give us a call. That way, we can help you straight away.')}</p>

                                
                                 </>
                                )}
                                {messages?.map((item) => (
                                    <div key={item.id} className={`${styles.bubble} ${item.source === 1 ? styles.bubbleTeam : styles.bubbleOwn}`}>
                                        <div className={styles.bubbleAuthor}>{item.source === 1 ? (item.user?.name ?? 'Leoprinting') : t('You')}</div>
                                        <div className={styles.bubbleContent}>{item.message}</div>
                                        <div className={styles.bubbleTime}>{getTimeAgo(item.createdAt)}</div>
                                    </div>
                                ))}
                            </div>
                            {justSent && (
                                <p className={styles.confirmation}>{t('Feel free to send us a message. We usually respond within 8 hours on working days.')}</p>
                            )}
                            {error && <p className={styles.composerError}>{error}</p>}
                            <form className={styles.composer} onSubmit={submitHandler}>
                                <textarea
                                    rows={1}
                                    value={inputValue}
                                    disabled={!canSend || sending}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    placeholder={t('Type your message...')}
                                />
                                <button type="submit" className={styles.sendButton} disabled={!canSend || sending || !inputValue.trim()}>
                                    {t('Send')}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            )}
        </div>
    )
}

PassiveChat.propTypes = {
    token: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        name: PropTypes.string,
    })),
    isApproved: PropTypes.bool,
    hidden: PropTypes.bool,
}

export default PassiveChat
